// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { ReactNode } from 'react';

type AttachmentType = 'file' | 'link';
export type AttachmentOwnerType =
  | 'asset'
  | 'project'
  | 'fulfillmentOrder'
  | 'purchaseOrder'
  | 'returnOrder';

interface AttachmentDeleteDialogBaseProps {
  attachmentType: AttachmentType;
  includeAuditNote?: boolean;
  isPending: boolean;
  isOpen: boolean;
  onExited?: () => void;
  onClose: () => void;
  onConfirm: () => void;
}

interface AttachmentDeleteDialogFrameProps
  extends AttachmentDeleteDialogBaseProps {
  children: ReactNode;
}

interface DeleteNamedAttachmentDialogProps
  extends AttachmentDeleteDialogBaseProps {
  attachmentName: string;
}

interface DeleteOwnerAttachmentDialogProps
  extends AttachmentDeleteDialogBaseProps {
  ownerType: AttachmentOwnerType;
}

const attachmentTypeLabels = {
  file: 'file',
  link: 'link',
} satisfies Record<AttachmentType, string>;

const attachmentOwnerLabels = {
  asset: 'asset',
  project: 'project',
  fulfillmentOrder: 'fulfillment order',
  purchaseOrder: 'purchase order',
  returnOrder: 'return order',
} satisfies Record<AttachmentOwnerType, string>;

function AttachmentDeleteDialogFrame(props: AttachmentDeleteDialogFrameProps) {
  const attachmentLabel = attachmentTypeLabels[props.attachmentType];
  const title = `Delete ${attachmentLabel}`;
  const transitionProps = props.onExited
    ? { onExited: props.onExited }
    : undefined;

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={props.isOpen}
      onClose={props.onClose}
      TransitionProps={transitionProps}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1}>
          {props.children}
          {props.includeAuditNote && (
            <Typography>
              (Note: your audit log will continue to show a record of the
              deleted {attachmentLabel}.)
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={props.onClose}>
          No, cancel
        </Button>
        <Button
          size="small"
          color="error"
          variant="contained"
          onClick={props.onConfirm}
          disabled={props.isPending}
        >
          Yes, delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function DeleteNamedAttachmentDialog(
  props: DeleteNamedAttachmentDialogProps
) {
  return (
    <AttachmentDeleteDialogFrame
      attachmentType={props.attachmentType}
      includeAuditNote={props.includeAuditNote}
      isPending={props.isPending}
      isOpen={props.isOpen}
      onClose={props.onClose}
      onConfirm={props.onConfirm}
      onExited={props.onExited}
    >
      <Typography>
        Are you sure you want to delete "{props.attachmentName}"? This action
        cannot be undone.
      </Typography>
    </AttachmentDeleteDialogFrame>
  );
}

export function DeleteOwnerAttachmentDialog(
  props: DeleteOwnerAttachmentDialogProps
) {
  const attachmentLabel = attachmentTypeLabels[props.attachmentType];
  const ownerLabel = attachmentOwnerLabels[props.ownerType];

  return (
    <AttachmentDeleteDialogFrame
      attachmentType={props.attachmentType}
      includeAuditNote={props.includeAuditNote}
      isPending={props.isPending}
      isOpen={props.isOpen}
      onClose={props.onClose}
      onConfirm={props.onConfirm}
      onExited={props.onExited}
    >
      <Typography>
        Are you sure you want to delete this {attachmentLabel}? The{' '}
        {attachmentLabel} will be permanently removed from the {ownerLabel}.
      </Typography>
    </AttachmentDeleteDialogFrame>
  );
}
